import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, CheckSquare } from 'lucide-react';
import { loadEventDraft, loadTaskDraft, clearEventDraft, clearTaskDraft, EventDraftData, TaskDraftData } from '../../utils/formDraft';

interface DraftCardProps {
  onOpenEventDraft: () => void;
  onOpenTaskDraft: () => void;
}

export const DraftCard: React.FC<DraftCardProps> = ({ onOpenEventDraft, onOpenTaskDraft }) => {
  const [eventDraft, setEventDraft] = useState<EventDraftData | null>(null);
  const [taskDraft, setTaskDraft] = useState<TaskDraftData | null>(null);

  const refresh = useCallback(() => {
    setEventDraft(loadEventDraft());
    setTaskDraft(loadTaskDraft());
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('mozayc:draft-changed', refresh);
    return () => window.removeEventListener('mozayc:draft-changed', refresh);
  }, [refresh]);

  if (!eventDraft && !taskDraft) return null;

  return (
    <div className="fixed bottom-4 right-4 flex gap-2 z-50 pointer-events-none">
      {eventDraft && (
        <div
          className="flex items-center gap-2.5 px-3.5 py-2.5 bg-[var(--modal-bg)] border border-[var(--border-primary)] rounded-xl cursor-pointer hover:bg-[var(--surface-secondary)] transition-colors pointer-events-auto animate-fadeIn"
          style={{ boxShadow: 'var(--shadow-lg)' }}
          onClick={onOpenEventDraft}
          role="button"
          aria-label={`Restore event draft: ${eventDraft.title || 'Untitled event'}`}
        >
          <Calendar className="w-3.5 h-3.5 text-[var(--accent-primary)] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-[var(--content-primary)] truncate max-w-[140px] leading-tight">
              {eventDraft.title || 'Untitled event'}
            </p>
            <p className="text-[10px] text-[var(--content-tertiary)] leading-tight">Event draft</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); clearEventDraft(); }}
            className="w-5 h-5 flex items-center justify-center rounded-md text-[var(--content-tertiary)] hover:text-[var(--content-primary)] hover:bg-[var(--surface-tertiary)] transition-colors flex-shrink-0 ml-0.5"
            aria-label="Discard event draft"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {taskDraft && (
        <div
          className="flex items-center gap-2.5 px-3.5 py-2.5 bg-[var(--modal-bg)] border border-[var(--border-primary)] rounded-xl cursor-pointer hover:bg-[var(--surface-secondary)] transition-colors pointer-events-auto animate-fadeIn"
          style={{ boxShadow: 'var(--shadow-lg)' }}
          onClick={onOpenTaskDraft}
          role="button"
          aria-label={`Restore task draft: ${taskDraft.title || 'Untitled task'}`}
        >
          <CheckSquare className="w-3.5 h-3.5 text-[var(--accent-primary)] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-[var(--content-primary)] truncate max-w-[140px] leading-tight">
              {taskDraft.title || 'Untitled task'}
            </p>
            <p className="text-[10px] text-[var(--content-tertiary)] leading-tight">Task draft</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); clearTaskDraft(); }}
            className="w-5 h-5 flex items-center justify-center rounded-md text-[var(--content-tertiary)] hover:text-[var(--content-primary)] hover:bg-[var(--surface-tertiary)] transition-colors flex-shrink-0 ml-0.5"
            aria-label="Discard task draft"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};
